(() => {
  const ACTIVE_CLASS = 'product-scroll-nav-active';
  const RESTORING_CLASS = 'product-scroll-nav-restoring';
  const HEADER_TRANSITION_MS = 180;
  const REVIEW_SELECTORS = ['#judgeme_product_reviews', '.jdgm-review-widget'];

  const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;

  const normalizeTarget = (value) => String(value || '').trim().replace(/^#/, '');

  const isRenderable = (element) => {
    if (!element || !element.isConnected) return false;
    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  };

  const findSection = (value) => {
    const key = normalizeTarget(value);
    if (!key) return null;

    const directIds = [key, `shopify-section-${key}`];
    for (const id of directIds) {
      const direct = document.getElementById(id);
      if (direct) return direct;
    }

    const elements = document.querySelectorAll('[id], [data-section-id]');
    for (const element of elements) {
      if (element.getAttribute('id')?.endsWith(`__${key}`)) return element;
      if (element.getAttribute('data-section-id') === key) return element;
    }

    return null;
  };

  const findAnchor = (value) => {
    const key = normalizeTarget(value);
    return key ? document.getElementById(key) : null;
  };

  const findReviews = () => {
    for (const selector of REVIEW_SELECTORS) {
      const element = document.querySelector(selector);
      if (element) return element;
    }
    return null;
  };

  class ProductScrollNav extends HTMLElement {
    constructor() {
      super();
      this._connected = false;
      this._listeners = [];
      this._targetRecords = [];
      this._resizeElements = new Set();
      this._intersectionObserver = null;
      this._resizeObserver = null;
      this._mutationObserver = null;
      this._frameId = null;
      this._headerTransitionTimer = null;
      this._visible = false;
      this._needsRefresh = true;
      this._needsObserverRebuild = true;
      this._observerSignature = '';
      this._form = null;
      this._variantChangeHandler = null;
      this._triggerFollowsPurchaseDock = false;

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._onResize.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onThemeEvent = this._onThemeEvent.bind(this);
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._isCampaign = this.dataset.mode === 'campaign';
      this._bar = this.querySelector('.product-scroll-nav__bar');
      this._variantOutput = this.querySelector('.product-scroll-nav__variant');

      if (this._isCampaign) document.documentElement.classList.add('campaign-scroll-nav-page');

      this._listen(this, 'click', this._onClick);
      this._listen(window, 'scroll', this._onScroll, { passive: true });
      this._listen(window, 'resize', this._onResize, { passive: true });
      this._listen(document, 'shopify:section:load', this._onThemeEvent);
      this._listen(document, 'shopify:section:reorder', this._onThemeEvent);
      this._listen(document, 'shopify:section:unload', this._onThemeEvent);

      if (typeof ResizeObserver === 'function') {
        this._resizeObserver = new ResizeObserver(() => {
          this._needsObserverRebuild = true;
          this._scheduleFrame();
        });
      }

      if (typeof MutationObserver === 'function') {
        this._mutationObserver = new MutationObserver(() => {
          this._needsRefresh = true;
          this._scheduleFrame();
        });
        this._mutationObserver.observe(document.body || document.documentElement, { childList: true, subtree: true });
      }

      this._updateVariant(this._readInitialVariant());
      this._cancelHeaderTransition();
      this._clearHeaderTransitionClasses();
      this.classList.remove('is-visible', 'is-exiting');
      this._setVisible(this._isCampaign);
      this.refresh();
    }

    disconnectedCallback() {
      this._connected = false;
      this._cancelHeaderTransition();
      document.querySelector('store-header')?.show?.();
      this._clearHeaderTransitionClasses();
      this.classList.remove('is-visible', 'is-exiting');
      this._cancelFrame();
      this._intersectionObserver?.disconnect();
      this._resizeObserver?.disconnect();
      this._mutationObserver?.disconnect();
      this._intersectionObserver = null;
      this._resizeObserver = null;
      this._mutationObserver = null;
      this._unbindProductForm();
      this._listeners.forEach(({ target, type, listener, options }) => target.removeEventListener(type, listener, options));
      this._listeners = [];
      this._setVisible(false, true);
      this._resizeElements.clear();
      if (this._isCampaign && !document.querySelector('product-scroll-nav[data-mode="campaign"]')) {
        document.documentElement.classList.remove('campaign-scroll-nav-page');
      }
    }

    refresh() {
      if (!this._connected) return;
      this._needsRefresh = true;
      this._scheduleFrame();
    }

    _listen(target, type, listener, options) {
      target.addEventListener(type, listener, options);
      this._listeners.push({ target, type, listener, options });
    }

    _onThemeEvent(event) {
      if (event.type === 'shopify:section:unload') {
        if (event.target === this || event.target?.contains?.(this)) {
          this._intersectionObserver?.disconnect();
          this._resizeObserver?.disconnect();
        }
        return;
      }
      this.refresh();
    }

    _onScroll() {
      this._scheduleFrame();
    }

    _onResize() {
      this._needsObserverRebuild = true;
      this._scheduleFrame();
    }

    _scheduleFrame() {
      if (!this._connected || this._frameId !== null) return;
      this._frameId = window.requestAnimationFrame(() => {
        this._frameId = null;
        this._runFrame();
      });
    }

    _cancelFrame() {
      if (this._frameId === null) return;
      window.cancelAnimationFrame(this._frameId);
      this._frameId = null;
    }

    _runFrame() {
      if (!this._connected) return;

      if (this._needsRefresh) {
        this._needsRefresh = false;
        this._resolveTargets();
        this._bindProductForm();
        this._updateResizeTargets();
        this._needsObserverRebuild = true;
      }

      if (this._needsObserverRebuild) this._rebuildIntersectionObserver();
      this._applyFrameState(this._readFrameState());
    }

    _resolveTargets() {
      const previousTargets = [this._triggerElement, ...this._targetRecords.map(record => record.target)];
      const previousTriggerFollowsPurchaseDock = this._triggerFollowsPurchaseDock;
      const productId = String(this.dataset.productId || '');
      const purchaseDock = productId
        ? Array.from(document.querySelectorAll('product-nav-purchase-dock')).find((dock) => {
            const dockSection = dock.closest('.yg-anchor-section');
            const purchaseBar = dockSection?.querySelector('[data-product-nav-purchase-bar]') || dock.querySelector('[data-product-nav-purchase-bar]');
            const productElement = purchaseBar?.querySelector('[data-product-id]') || purchaseBar;
            return String(productElement?.dataset.productId || '') === productId;
          })
        : null;
      this._triggerFollowsPurchaseDock = Boolean(purchaseDock);
      this._triggerElement = purchaseDock
        ? purchaseDock.closest('.yg-anchor-section') || purchaseDock
        : findSection(this.dataset.triggerTarget);
      this._targetRecords = Array.from(this.querySelectorAll('[data-product-nav-link]')).map((link) => {
        const kind = link.dataset.kind || 'section';
        let target = null;
        if (kind === 'section') target = findSection(link.dataset.target);
        if (kind === 'anchor') target = findAnchor(link.dataset.target);
        if (kind === 'reviews') target = findReviews();
        return { link, kind, target, available: kind === 'top', rect: null };
      });
      const targets = [this._triggerElement, ...this._targetRecords.map(record => record.target)];
      if (previousTriggerFollowsPurchaseDock !== this._triggerFollowsPurchaseDock || targets.length !== previousTargets.length || targets.some((target, i) => target !== previousTargets[i])) this._observerSignature = '';
    }

    _measureTopOffset() {
      if (this._isCampaign) return 0;
      return ['.shopify-section--announcement-bar', '.shopify-section--header'].reduce((total, selector) => {
        const element = document.querySelector(selector);
        if (!element) return total;
        return total + element.getBoundingClientRect().height;
      }, 0);
    }

    _measureNavHeight() {
      return this._isCampaign ? this.offsetHeight : (this._bar?.offsetHeight || this.offsetHeight || 0);
    }

    _updateResizeTargets() {
      if (!this._resizeObserver) return;
      const nextElements = new Set([
        this._bar,
        document.querySelector('main'),
        document.querySelector('.shopify-section--announcement-bar'),
        document.querySelector('.shopify-section--header'),
        this._triggerElement,
        ...this._targetRecords.map((record) => record.target)
      ].filter(Boolean));

      this._resizeElements.forEach((element) => {
        if (!nextElements.has(element)) this._resizeObserver.unobserve(element);
      });
      nextElements.forEach((element) => {
        if (!this._resizeElements.has(element)) this._resizeObserver.observe(element);
      });
      this._resizeElements = nextElements;
    }

    _rebuildIntersectionObserver() {
      this._needsObserverRebuild = false;
      if (typeof IntersectionObserver !== 'function') return;

      const topOffset = Math.ceil(this._measureTopOffset());
      const barHeight = this._measureNavHeight();
      const readingLine = barHeight + 12;
      const observerTop = Math.min(readingLine, Math.max(1, window.innerHeight - 1));
      const observerBottom = Math.max(0, window.innerHeight - observerTop - 1);
      const observedTargets = [this._triggerElement, ...this._targetRecords.map((record) => record.target)].filter(Boolean);
      const signature = `${topOffset}|${barHeight}|${observedTargets.map((element) => element.id || element.tagName).join('|')}`;
      if (signature === this._observerSignature && this._intersectionObserver) return;

      this._intersectionObserver?.disconnect();
      this._observerSignature = signature;
      this._intersectionObserver = new IntersectionObserver(() => {
        this._scheduleFrame();
      }, {
        rootMargin: `-${observerTop}px 0px -${observerBottom}px 0px`,
        threshold: [0, 1]
      });
      observedTargets.forEach((target) => this._intersectionObserver.observe(target));
    }

    _readFrameState() {
      const scrollY = window.scrollY || 0;
      const topOffset = this._triggerFollowsPurchaseDock ? 0 : this._measureTopOffset();
      const navHeight = this._measureNavHeight();
      const baseline = navHeight + 13;
      const triggerRect = this._triggerElement?.getBoundingClientRect();
      const triggerBoundary = this._visible ? Math.max(topOffset, navHeight + 12) + 4 : topOffset;
      const passedTrigger = this._isCampaign || (this._triggerFollowsPurchaseDock
        ? Boolean(triggerRect && triggerRect.top < 0)
        : Boolean(triggerRect && triggerRect.top <= triggerBoundary));

      this._targetRecords.forEach((record) => {
        record.available = record.kind === 'top' || isRenderable(record.target);
        record.rect = record.available && record.target ? record.target.getBoundingClientRect() : null;
      });

      const nonTop = this._targetRecords.filter((record) => record.available && record.kind !== 'top' && record.rect);
      const crossed = nonTop.filter((record) => record.rect.top <= baseline);
      let active = crossed.reduce((current, record) => {
        if (!current || record.rect.top > current.rect.top) return record;
        return current;
      }, null);

      const atPageEnd = scrollY + window.innerHeight >= (document.scrollingElement?.scrollHeight || document.documentElement.scrollHeight) - 2;
      const reviewRecord = this._targetRecords.find((record) => record.kind === 'reviews' && record.available);
      if (atPageEnd && reviewRecord) active = reviewRecord;
      if (!active) active = nonTop[0] || this._targetRecords.find((record) => record.available) || null;

      const links = this.querySelector('.product-scroll-nav__links');
      let linkScroll = null;
      if (active && active.link !== this._activeLink && links) {
        const linkRect = active.link.getBoundingClientRect();
        const listRect = links.getBoundingClientRect();
        if (linkRect.left < listRect.left || linkRect.right > listRect.right) linkScroll = links.scrollLeft + linkRect.left - listRect.left - (listRect.width - linkRect.width) / 2;
      }
      return { passedTrigger, active, links, linkScroll };
    }

    _applyFrameState(state) {
      this._targetRecords.forEach((record) => {
        record.link.hidden = !record.available;
        record.link.setAttribute('aria-hidden', String(!record.available));
        record.link.setAttribute('aria-current', record === state.active ? 'true' : 'false');
      });
      this._activeLink = state.active?.link;
      if (state.linkScroll !== null) state.links.scrollTo({left: state.linkScroll, behavior: reducedMotion() ? 'auto' : 'smooth'});

      if (!this._isCampaign && !this._triggerElement) {
        this._setVisible(false);
        return;
      }

      if (this._visible !== state.passedTrigger) this._setVisible(state.passedTrigger);
    }

    _setVisible(visible, immediate = false) {
      const wasVisible = this._visible;
      this._visible = visible;
      const root = document.documentElement;

      if (this._isCampaign) {
        this.classList.toggle('is-visible', visible);
        this.classList.remove('is-exiting');
        this.toggleAttribute('inert', !visible);
        this.setAttribute('aria-hidden', String(!visible));
        return;
      }

      if (visible) {
        this._cancelHeaderTransition();
        root.classList.remove(RESTORING_CLASS);
        root.classList.add(ACTIVE_CLASS);
        this.classList.remove('is-exiting');
        this.classList.add('is-visible');
      } else {
        this.classList.remove('is-visible');
      }

      this.toggleAttribute('inert', !visible);
      this.setAttribute('aria-hidden', String(!visible));

      if (!visible && wasVisible) {
        if (this.contains(document.activeElement) && typeof document.activeElement.blur === 'function') {
          document.activeElement.blur();
        }

        if (immediate || reducedMotion() || !this._connected) {
          if (this._connected) document.querySelector('store-header')?.show?.();
          this._cancelHeaderTransition();
          this.classList.remove('is-exiting');
          this._clearHeaderTransitionClasses();
          return;
        }

        this._cancelHeaderTransition();
        this.classList.add('is-exiting');
        this._headerTransitionTimer = window.setTimeout(() => {
          this._headerTransitionTimer = null;
          if (!this._connected || this._visible || reducedMotion()) {
            this.classList.remove('is-exiting');
            this._clearHeaderTransitionClasses();
            return;
          }

          this.classList.remove('is-exiting');
          document.querySelector('store-header')?.show?.();
          root.classList.remove(ACTIVE_CLASS);
          root.classList.add(RESTORING_CLASS);
          this._headerTransitionTimer = window.setTimeout(() => {
            this._headerTransitionTimer = null;
            if (!this._connected || this._visible) {
              this._clearHeaderTransitionClasses();
              return;
            }
            root.classList.remove(RESTORING_CLASS);
          }, HEADER_TRANSITION_MS);
        }, HEADER_TRANSITION_MS);
      }
    }

    _cancelHeaderTransition() {
      if (this._headerTransitionTimer === null) return;
      window.clearTimeout(this._headerTransitionTimer);
      this._headerTransitionTimer = null;
    }

    _clearHeaderTransitionClasses() {
      document.documentElement.classList.remove(ACTIVE_CLASS, RESTORING_CLASS);
    }

    _readInitialVariant() {
      const script = this.querySelector('script[data-product-nav-variant]')?.textContent?.trim();
      if (!script) return null;
      try {
        return JSON.parse(script);
      } catch {
        return null;
      }
    }

    _findProductForm() {
      const productId = String(this.dataset.productId || '');
      if (!productId) return null;
      return Array.from(document.forms).find((form) => {
        const id = form.getAttribute('id') || '';
        return id.startsWith(`product-form-${productId}-`) || id.includes(`productid-${productId}`);
      }) || null;
    }

    _bindProductForm() {
      const form = this._findProductForm();
      if (form === this._form) return;
      this._unbindProductForm();
      if (!form) return;
      this._form = form;
      this._variantChangeHandler = (event) => {
        const detail = event.detail || {};
        if (detail.product?.id && String(detail.product.id) !== String(this.dataset.productId)) return;
        if (detail.variant) this._updateVariant(detail.variant);
      };
      this._form.addEventListener('variant:change', this._variantChangeHandler);
    }

    _unbindProductForm() {
      if (this._form && this._variantChangeHandler) {
        this._form.removeEventListener('variant:change', this._variantChangeHandler);
      }
      this._form = null;
      this._variantChangeHandler = null;
    }

    _updateVariant(variant) {
      if (!this._variantOutput) return;
      const title = String(variant?.title || '').trim();
      const show = this.dataset.showVariant !== 'false' && title && title.toLowerCase() !== 'default title';
      this._variantOutput.textContent = show ? title : '';
      this._variantOutput.hidden = !show;
    }

    _onClick(event) {
      const link = event.target.closest?.('[data-product-nav-link]');
      if (!link || !this.contains(link) || link.hidden) return;
      const record = this._targetRecords.find((item) => item.link === link);
      if (!record) return;
      event.preventDefault();
      this._scrollToRecord(record);
    }

    _scrollToRecord(record) {
      let targetTop = 0;
      if (record.kind !== 'top') {
        if (!record.target || !isRenderable(record.target)) return;
        const sectionTop = window.scrollY + record.target.getBoundingClientRect().top;
        targetTop = sectionTop - this._measureNavHeight() - 12;
        if (this._triggerFollowsPurchaseDock && this._triggerElement) {
          const triggerTop = window.scrollY + this._triggerElement.getBoundingClientRect().top;
          if (sectionTop >= triggerTop) targetTop = Math.max(targetTop, triggerTop + 1);
        }
      }
      const scroller = document.scrollingElement || document.documentElement;
      const maxTop = Math.max(0, scroller.scrollHeight - window.innerHeight);
      try {
        window.scrollTo({
          top: Math.min(maxTop, Math.max(0, targetTop)),
          behavior: reducedMotion() ? 'auto' : 'smooth'
        });
      } catch {
        window.scrollTo(0, Math.min(maxTop, Math.max(0, targetTop)));
      }
    }
  }

  class ProductNavPurchaseDock extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._section = this.closest('.yg-anchor-section');
      this._bar = this._section?.querySelector('[data-product-nav-purchase-bar]') || this.querySelector('[data-product-nav-purchase-bar]');
      this._variant = this._bar?.querySelector('[data-product-nav-purchase-variant], .yg-product-description__variant');
      this._positionTarget = this._section || this;
      this._originalDisplay = this._bar?.style.display || '';
      this._originalVariantHidden = this._variant?.hasAttribute('hidden') || false;
      this._frameId = null;
      this._onScroll = () => {
        if (this._frameId !== null) return;
        this._frameId = window.requestAnimationFrame(() => {
          this._frameId = null;
          this._evaluate();
        });
      };
      this._setVisible(false);
      this._observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(() => this._evaluate(), {
        rootMargin: '-1px 0px 0px 0px',
        threshold: [0, 1]
      }) : null;
      this._observer?.observe(this._positionTarget);
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._evaluate();
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      this._observer = null;
      window.removeEventListener('scroll', this._onScroll);
      if (this._frameId !== null) window.cancelAnimationFrame(this._frameId);
      if (this._bar) this._bar.style.display = this._originalDisplay;
      if (this._variant) this._variant.toggleAttribute('hidden', this._originalVariantHidden);
      this._connected = false;
    }

    _evaluate() {
      if (!this._bar || !this._positionTarget) return;
      const passed = this._positionTarget.getBoundingClientRect().top < 0;
      this._setVisible(passed);
    }

    _setVisible(visible) {
      if (!this._bar) return;
      this._bar.style.display = visible ? 'flex' : 'none';
      if (this._variant) this._variant.toggleAttribute('hidden', !visible);
    }
  }

  if (!window.customElements.get('product-scroll-nav')) {
    window.customElements.define('product-scroll-nav', ProductScrollNav);
  }
  if (!window.customElements.get('product-nav-purchase-dock')) {
    window.customElements.define('product-nav-purchase-dock', ProductNavPurchaseDock);
  }
})();
